/* ==========================================================================
   Yokabag Voyage — global.js
   Composants Web (custom elements) du thème. Aucune dépendance externe.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  if (window.Shopify && window.Shopify.designMode) document.documentElement.classList.add('shopify-design-mode');

  /* ---------------------------------------------------------------- Utils */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';
  let trapHandler = null;
  const trapFocus = (container, elementToFocus) => {
    releaseFocus();
    const els = () => $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
    trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const list = els();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', trapHandler);
    (elementToFocus || els()[0] || container).focus({ preventScroll: true });
  };
  const releaseFocus = () => { if (trapHandler) document.removeEventListener('keydown', trapHandler); trapHandler = null; };

  const toast = (message) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 2600);
  };
  window.YokaToast = toast;

  /* ---------------------------------------------------------------- Reveal */
  const initReveal = (root = document) => {
    const items = $$('.reveal:not(.is-visible)', root);
    if (!('IntersectionObserver' in window) || !document.body.classList.contains('animations-on')) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => io.observe(el));
  };
  document.addEventListener('DOMContentLoaded', () => initReveal());
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  /* ---------------------------------------------------------------- Drawers */
  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('scroll-locked');
      const panel = $('.drawer__panel', this) || this;
      setTimeout(() => trapFocus(panel, $('[data-autofocus]', panel)), 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('scroll-locked');
      releaseFocus();
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('theme-drawer', ThemeDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    drawer.open(opener);
  });

  /* ---------------------------------------------------------------- Cart API */
  const Cart = {
    sectionsToRender() {
      const ids = ['cart-icon-bubble'];
      if ($('cart-drawer')) ids.push('cart-drawer');
      return ids;
    },
    async add(formData) {
      formData.append('sections', this.sectionsToRender().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return data;
    },
    async change(line, quantity) {
      const sections = this.sectionsToRender();
      const mainCart = $('[data-main-cart]');
      if (mainCart) sections.push(mainCart.dataset.sectionId);
      const res = await fetch(`${theme.routes.cartChange}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ line, quantity, sections: sections.join(','), sections_url: window.location.pathname }) });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: data }));
      return data;
    },
    async updateNote(note) {
      await fetch(`${theme.routes.cartUpdate}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ note }) });
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-icon-bubble') {
          $$('[data-cart-bubble]').forEach((el) => { const n = $('[data-cart-bubble]', doc); if (n) el.innerHTML = n.innerHTML; });
        } else if (id === 'cart-drawer') {
          const target = $('[data-cart-drawer-inner]');
          const source = $('[data-cart-drawer-inner]', doc);
          if (target && source) target.innerHTML = source.innerHTML;
        } else {
          const target = $(`[data-main-cart][data-section-id="${id}"]`);
          const source = $('[data-main-cart]', doc);
          if (target && source) target.innerHTML = source.innerHTML;
        }
      });
    },
    afterAdd(opener) {
      const drawer = $('cart-drawer');
      if (theme.cartType === 'drawer' && drawer) drawer.open(opener);
      else if (theme.cartType === 'page') window.location.href = theme.routes.cart;
    }
  };
  window.YokaCart = Cart;

  class CartDrawer extends ThemeDrawer {}
  customElements.define('cart-drawer', CartDrawer);

  /* Quantités et suppression dans le panier (tiroir + page) */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.onChange = debounce((input) => this.update(input.dataset.line, parseInt(input.value, 10) || 0, input), 350);
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-cart-qty]')) this.onChange(e.target);
        if (e.target.matches('[data-cart-note]')) Cart.updateNote(e.target.value);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.update(remove.dataset.line, 0, remove); }
      });
    }
    async update(line, qty, el) {
      const item = el && el.closest('[data-cart-line]');
      if (item) item.classList.add('is-updating');
      try { await Cart.change(parseInt(line, 10), qty); }
      catch (err) { toast(err.message); if (item) item.classList.remove('is-updating'); }
    }
  }
  customElements.define('cart-items', CartItems);

  /* Suggestions dans le panier (recommandations produit) */
  class CartUpsell extends HTMLElement {
    connectedCallback() { this.load(); }
    async load() {
      const productId = this.dataset.productId;
      if (!productId || this.dataset.source === 'collection') return;
      try {
        const url = `${theme.routes.recommendations}?product_id=${productId}&limit=6&section_id=cart-upsell&intent=related`;
        const html = await (await fetch(url)).text();
        const inner = $('[data-upsell-inner]', parseHTML(html));
        const target = $('[data-upsell-inner]', this);
        if (inner && target && inner.children.length) { target.innerHTML = inner.innerHTML; this.hidden = false; }
      } catch (e) { /* silencieux */ }
    }
  }
  customElements.define('cart-upsell', CartUpsell);

  /* ---------------------------------------------------------------- Quantité */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn) return;
        e.preventDefault();
        const step = parseInt(this.input.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const current = parseInt(this.input.value, 10) || 0;
        const next = btn.dataset.qty === 'plus' ? current + step : current - step;
        this.input.value = Math.min(max, Math.max(min, next));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------------------------------------------------------------- Formulaire produit */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const submitter = e.submitter || $('[type="submit"]', this.form);
      const buttons = $$(`[type="submit"][form="${this.form.id}"], [type="submit"]`, document).filter((b) => b.form === this.form);
      if (submitter && submitter.getAttribute('aria-disabled') === 'true') return;
      const errorBox = $('[data-form-error]', this);
      if (errorBox) errorBox.hidden = true;
      buttons.forEach((b) => b.classList.add('is-loading'));
      try {
        await Cart.add(new FormData(this.form));
        const quick = this.closest('theme-drawer#QuickAdd');
        if (quick) quick.close();
        Cart.afterAdd(submitter);
      } catch (err) {
        if (errorBox) { errorBox.hidden = false; $('[data-form-error-text]', errorBox).textContent = err.message; }
        else toast(err.message);
      } finally {
        buttons.forEach((b) => b.classList.remove('is-loading'));
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------------------------------------------------------------- Sélecteur de variantes */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = $('script[type="application/json"]', this);
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl !== 'false';
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }
    selectedOptions() {
      return $$('[data-option-index]', this).map((group) => {
        const checked = $('input:checked', group);
        const select = $('select', group);
        return checked ? checked.value : select ? select.value : null;
      });
    }
    markAvailability() {
      const selected = this.selectedOptions();
      $$('[data-option-index]', this).forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);
        $$('input[type="radio"]', group).forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const available = this.variants.some((v) => v.available && v.options[index] === input.value && v.options.every((o, i) => i === index || o === candidate[i]));
          input.closest('.option-value')?.classList.toggle('is-unavailable', !available);
        });
        const label = $('[data-selected-value]', group);
        if (label) label.textContent = selected[index] || '';
      });
    }
    onChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      this.markAvailability();
      const scope = this.closest('[data-product-scope]') || document;
      const idInputs = $$('input[name="id"]', scope);
      if (!variant) {
        $$('[data-add-button]', scope).forEach((b) => { b.setAttribute('aria-disabled', 'true'); const t = $('[data-add-text]', b); if (t) t.textContent = theme.strings.unavailable; });
        return;
      }
      idInputs.forEach((i) => { i.value = variant.id; i.dispatchEvent(new Event('change', { bubbles: true })); });
      if (this.updateUrl) window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
      scope.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      this.renderSection(variant, scope);
    }
    async renderSection(variant, scope) {
      if (!this.sectionId) return;
      this.abort?.abort();
      this.abort = new AbortController();
      try {
        const html = await (await fetch(`${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`, { signal: this.abort.signal })).text();
        const doc = parseHTML(html);
        $$('[data-update]', scope).forEach((el) => {
          const fresh = $(`[data-update="${el.dataset.update}"]`, doc);
          if (fresh) el.innerHTML = fresh.innerHTML;
          if (fresh) {
            ['aria-disabled', 'hidden'].forEach((attr) => {
              if (fresh.hasAttribute(attr)) el.setAttribute(attr, fresh.getAttribute(attr)); else el.removeAttribute(attr);
            });
          }
        });
      } catch (e) { /* requête annulée */ }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------------------------------------------------------------- Galerie produit */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewer = $('[data-gallery-viewer]', this);
      this.thumbs = $$('[data-thumb]', this);
      this.counter = $('[data-gallery-current]', this);
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(t.dataset.thumb)));
      $$('[data-gallery-prev], [data-gallery-next]', this).forEach((b) => b.addEventListener('click', () => this.step(b.hasAttribute('data-gallery-next') ? 1 : -1)));
      this.thumbList = $('.gallery__thumbs', this);
      $$('[data-thumbs-prev], [data-thumbs-next]', this).forEach((b) => b.addEventListener('click', () => {
        if (!this.thumbList) return;
        const dir = b.hasAttribute('data-thumbs-next') ? 1 : -1;
        this.thumbList.scrollBy({ left: dir * this.thumbList.clientWidth * 0.8, behavior: 'smooth' });
      }));
      this.thumbList?.addEventListener('scroll', () => this.updateThumbArrows(), { passive: true });
      this.updateThumbArrows();
      if (this.viewer && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting && en.intersectionRatio > 0.6) this.setActive(en.target.dataset.mediaId); });
        }, { root: this.viewer.scrollWidth > this.viewer.clientWidth ? this.viewer : null, threshold: [0.6] });
        $$('[data-media-id]', this.viewer).forEach((s) => io.observe(s));
      }
      const scope = this.closest('[data-product-scope]');
      if (scope) scope.addEventListener('variant:change', (e) => {
        const media = e.detail.variant && e.detail.variant.featured_media;
        if (media) this.goTo(String(media.id), 'instant');
      });
      const initial = this.dataset.initial;
      const first = this.slides()[0];
      if (initial && first && first.dataset.mediaId !== initial) requestAnimationFrame(() => this.goTo(initial, 'auto'));
      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) this.openLightbox(zoom.dataset.zoom);
      });
    }
    slides() { return $$('[data-media-id]', this.viewer); }
    goTo(id, behavior = 'smooth') {
      const slide = this.slides().find((s) => s.dataset.mediaId === String(id));
      if (!slide) return;
      if (this.viewer.scrollWidth > this.viewer.clientWidth + 4) this.viewer.scrollTo({ left: slide.offsetLeft - this.viewer.offsetLeft, behavior });
      else if (window.matchMedia('(min-width: 990px)').matches && this.dataset.layout === 'grid') slide.scrollIntoView({ behavior, block: 'center' });
      this.setActive(id);
    }
    step(dir) {
      const slides = this.slides();
      const i = slides.findIndex((s) => s.dataset.mediaId === this.activeId);
      const next = slides[(Math.max(0, i) + dir + slides.length) % slides.length];
      if (next) this.goTo(next.dataset.mediaId);
    }
    setActive(id) {
      this.activeId = String(id);
      const slides = this.slides();
      slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === this.activeId));
      this.thumbs.forEach((t) => {
        const on = t.dataset.thumb === this.activeId;
        t.setAttribute('aria-current', on ? 'true' : 'false');
        if (on) this.revealThumb(t);
      });
      if (this.counter) this.counter.textContent = slides.findIndex((s) => s.dataset.mediaId === this.activeId) + 1;
    }
    revealThumb(t) {
      // Défile uniquement le bandeau de vignettes (jamais la page).
      const list = this.thumbList;
      if (!list) return;
      const lr = list.getBoundingClientRect();
      const tr = t.getBoundingClientRect();
      if (list.scrollWidth > list.clientWidth) {
        if (tr.left < lr.left) list.scrollBy({ left: tr.left - lr.left - 8, behavior: 'smooth' });
        else if (tr.right > lr.right) list.scrollBy({ left: tr.right - lr.right + 8, behavior: 'smooth' });
      } else if (list.scrollHeight > list.clientHeight) {
        if (tr.top < lr.top) list.scrollBy({ top: tr.top - lr.top - 8, behavior: 'smooth' });
        else if (tr.bottom > lr.bottom) list.scrollBy({ top: tr.bottom - lr.bottom + 8, behavior: 'smooth' });
      }
    }
    updateThumbArrows() {
      const list = this.thumbList;
      if (!list) return;
      const max = list.scrollWidth - list.clientWidth;
      const prev = $('[data-thumbs-prev]', this);
      const next = $('[data-thumbs-next]', this);
      if (prev) prev.disabled = list.scrollLeft <= 2;
      if (next) next.disabled = list.scrollLeft >= max - 2;
      [prev, next].forEach((b) => { if (b) b.hidden = max <= 2; });
    }
    openLightbox(id) {
      const modal = $('[data-lightbox]', this);
      if (!modal) return;
      modal.classList.add('is-open');
      document.body.classList.add('scroll-locked');
      const target = $(`[data-lightbox-item="${id}"]`, modal);
      setTimeout(() => { if (target) target.scrollIntoView({ block: 'start' }); trapFocus($('.modal__content', modal), $('[data-lightbox-close]', modal)); }, 30);
      const close = () => { modal.classList.remove('is-open'); document.body.classList.remove('scroll-locked'); releaseFocus(); modal.removeEventListener('click', onClick); document.removeEventListener('keydown', onKey); };
      const onClick = (e) => { if (e.target.closest('[data-lightbox-close]') || e.target.classList.contains('modal__overlay')) close(); };
      const onKey = (e) => { if (e.key === 'Escape') close(); };
      modal.addEventListener('click', onClick);
      document.addEventListener('keydown', onKey);
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------------------------------------------------------------- Barre d'achat fixe */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const scope = this.closest('[data-product-scope]') || document;
      const anchor = $('[data-main-atc]', scope);
      if (!anchor || !('IntersectionObserver' in window)) return;
      const footer = $('.site-footer');
      let anchorHidden = false; let footerVisible = false;
      const update = () => { this.classList.toggle('is-visible', anchorHidden && !footerVisible); };
      new IntersectionObserver(([en]) => { anchorHidden = !en.isIntersecting && en.boundingClientRect.top < 0; update(); }).observe(anchor);
      if (footer) new IntersectionObserver(([en]) => { footerVisible = en.isIntersecting; update(); }).observe(footer);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------------------------------------------------------------- Estimation de livraison + compte à rebours */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() { this.render(); this.timer = setInterval(() => this.render(), 1000); }
    disconnectedCallback() { clearInterval(this.timer); }
    shopNow() {
      // Heure « murale » de la boutique, quel que soit le fuseau du visiteur.
      const now = new Date();
      const tz = this.dataset.timezone;
      if (!tz) return now;
      try {
        const p = {};
        new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
          .formatToParts(now).forEach((x) => { p[x.type] = x.value; });
        return new Date(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
      } catch (e) { return now; }
    }
    isWorkday(d) { return !this.skip || (d.getDay() !== 0 && d.getDay() !== 6); }
    addBusinessDays(date, days) {
      const d = new Date(date);
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        if (this.isWorkday(d)) added += 1;
      }
      return d;
    }
    nextCutoff(now) {
      const d = new Date(now);
      d.setHours(this.cutoff, 0, 0, 0);
      for (let i = 0; i < 10 && (d <= now || !this.isWorkday(d)); i += 1) { d.setDate(d.getDate() + 1); d.setHours(this.cutoff, 0, 0, 0); }
      return d;
    }
    dayLabel(now, target) {
      const days = Math.round((new Date(target).setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / 864e5);
      if (days <= 0) return this.dataset.today;
      if (days === 1) return this.dataset.tomorrow;
      return new Intl.DateTimeFormat(theme.locale || 'fr', { weekday: 'long' }).format(target);
    }
    setTime(el, diff) {
      const total = Math.max(0, Math.floor(diff / 1000));
      const parts = [['h', Math.floor(total / 3600)], ['m', Math.floor(total / 60) % 60], ['s', total % 60]];
      if (!el.firstChild) {
        el.innerHTML = parts.map(([u]) => `<span class="dt" data-u="${u}"><span class="dt__n"><span></span></span><span class="dt__u">${u}</span></span>`).join('');
      }
      parts.forEach(([u, v], i) => {
        const group = el.querySelector(`[data-u="${u}"]`);
        const n = group.firstChild;
        const txt = i === 0 ? String(v) : String(v).padStart(2, '0');
        group.hidden = u === 'h' && v === 0;
        if (n.firstChild.textContent === txt) return;
        const first = n.firstChild.textContent === '';
        n.firstChild.textContent = txt;
        if (first) return;
        n.classList.remove('is-tick');
        void n.offsetWidth;
        n.classList.add('is-tick');
      });
    }
    render() {
      const ds = this.dataset;
      this.cutoff = parseInt(ds.cutoff, 10);
      this.skip = ds.skipWeekends === 'true';
      const now = this.shopNow();
      const target = this.nextCutoff(now);
      const sameDay = target.toDateString() === now.toDateString();
      const procMin = parseInt(ds.processingMin, 10) || 0;
      const procMax = parseInt(ds.processingMax, 10) || procMin;
      const extra = sameDay ? 0 : 1;
      const range = $('[data-delivery-range]', this);
      if (range) {
        const from = this.addBusinessDays(now, procMin + extra + (parseInt(ds.shippingMin, 10) || 0));
        const to = this.addBusinessDays(now, procMax + extra + (parseInt(ds.shippingMax, 10) || 0));
        const fmt = new Intl.DateTimeFormat(theme.locale || 'fr', { weekday: 'short', day: 'numeric', month: 'short' });
        const txt = from.toDateString() === to.toDateString() ? fmt.format(from) : `${fmt.format(from)} – ${fmt.format(to)}`;
        if (range.textContent !== txt) range.textContent = txt;
      }
      const countdown = $('[data-delivery-countdown]', this);
      if (!countdown) return;
      if (!sameDay && ds.hasDay !== 'true') { countdown.hidden = true; return; }
      const day = $('[data-delivery-day]', countdown);
      if (day) { const label = this.dayLabel(now, target); if (day.textContent !== label) day.textContent = label; }
      const time = $('[data-delivery-time]', countdown);
      if (time) this.setTime(time, target - now);
      countdown.hidden = false;
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------------------------------------------------------------- Compte à rebours */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.end).getTime();
      if (Number.isNaN(this.end)) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const diff = this.end - Date.now();
      if (diff <= 0) { clearInterval(this.timer); if (this.dataset.hideExpired === 'true') this.closest('[data-countdown-wrapper]')?.setAttribute('hidden', ''); return; }
      const parts = { days: Math.floor(diff / 864e5), hours: Math.floor(diff / 36e5) % 24, minutes: Math.floor(diff / 6e4) % 60, seconds: Math.floor(diff / 1e3) % 60 };
      Object.entries(parts).forEach(([k, v]) => { const el = $(`[data-${k}]`, this); if (el) el.textContent = String(v).padStart(2, '0'); });
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------------------------------------------------------------- Copier un code promo */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    e.preventDefault();
    try { await navigator.clipboard.writeText(btn.dataset.copy); } catch (err) { /* ignore */ }
    btn.classList.add('is-copied');
    toast(theme.strings.copied);
    setTimeout(() => btn.classList.remove('is-copied'), 2000);
  });

  /* ---------------------------------------------------------------- Code promo à gratter */
  class CouponScratch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.canvas = $('[data-foil]', this);
      this.key = `yk-coupon-${this.dataset.code}`;
      let seen = false;
      try { seen = this.dataset.remember === 'true' && localStorage.getItem(this.key) === '1'; } catch (e) { /* ignore */ }
      if (seen) { this.reveal({ silent: true }); return; }
      this.addEventListener('click', (e) => {
        if (this.revealed) return;
        if (this.dragged) { this.dragged = false; return; }
        e.preventDefault();
        this.autoScratch(e.detail === 0);
      });
      if (!this.canvas || !this.canvas.getContext) return;
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      this.paint();
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => { if (!this.revealed && !this.touched && !this.animating) this.paint(); });
        this.ro.observe(this.canvas);
      }
      this.canvas.addEventListener('pointerdown', (e) => this.start(e));
      this.canvas.addEventListener('pointermove', (e) => this.move(e));
      ['pointerup', 'pointercancel'].forEach((t) => this.canvas.addEventListener(t, () => this.end()));
    }
    disconnectedCallback() { this.ro?.disconnect(); }
    paint() {
      const c = this.canvas;
      const r = c.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      c.width = Math.round(r.width * dpr);
      c.height = Math.round(r.height * dpr);
      const x = this.ctx;
      const w = r.width;
      const h = r.height;
      this.w = w; this.h = h;
      x.setTransform(dpr, 0, 0, dpr, 0, 0);
      x.globalCompositeOperation = 'source-over';
      const base = getComputedStyle(this).getPropertyValue('--foil').trim() || '#c9c3b8';
      x.fillStyle = base;
      x.fillRect(0, 0, w, h);
      // reflet métallique
      const g = x.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, 'rgba(255,255,255,0.4)');
      g.addColorStop(0.42, 'rgba(255,255,255,0.05)');
      g.addColorStop(0.5, 'rgba(255,255,255,0.45)');
      g.addColorStop(0.58, 'rgba(0,0,0,0.04)');
      g.addColorStop(1, 'rgba(255,255,255,0.3)');
      x.fillStyle = g;
      x.fillRect(0, 0, w, h);
      // grain
      for (let i = 0; i < (w * h) / 9; i += 1) {
        x.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.06)';
        x.fillRect(Math.random() * w, Math.random() * h, 1, 1);
      }
      // hachures fines
      x.strokeStyle = 'rgba(255,255,255,0.14)';
      x.lineWidth = 1;
      for (let i = -h; i < w; i += 4) { x.beginPath(); x.moveTo(i, h); x.lineTo(i + h, 0); x.stroke(); }
      // libellé
      const label = (this.dataset.label || '').toUpperCase();
      if (label) {
        const hex = base.replace('#', '');
        const full = hex.length === 3 ? hex.split('').map((ch) => ch + ch).join('') : hex;
        const rgb = [0, 2, 4].map((o) => parseInt(full.substr(o, 2), 16) || 0);
        const light = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000 > 140;
        x.fillStyle = light ? 'rgba(20,19,18,0.8)' : 'rgba(255,255,255,0.92)';
        x.font = `700 10.5px ${getComputedStyle(this).fontFamily}`;
        if ('letterSpacing' in x) x.letterSpacing = '1.8px';
        x.textAlign = 'center';
        x.textBaseline = 'middle';
        x.fillText(label, w / 2, h / 2 + 0.5);
      }
      this.classList.add('is-ready');
    }
    point(e) { const r = this.canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }
    start(e) {
      if (this.revealed || this.animating) return;
      this.scratching = true;
      this.moved = 0;
      this.last = this.point(e);
      try { this.canvas.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    }
    move(e) {
      if (!this.scratching) return;
      const p = this.point(e);
      this.moved += Math.hypot(p.x - this.last.x, p.y - this.last.y);
      if (this.moved > 10) { this.dragged = true; this.touched = true; }
      this.stroke(this.last, p, 22);
      this.last = p;
    }
    end() {
      if (!this.scratching) return;
      this.scratching = false;
      if (this.dragged && this.cleared() > 0.4) this.reveal();
    }
    stroke(a, b, width) {
      const x = this.ctx;
      x.globalCompositeOperation = 'destination-out';
      x.lineCap = 'round';
      x.lineJoin = 'round';
      x.lineWidth = width;
      x.beginPath();
      x.moveTo(a.x, a.y);
      x.lineTo(b.x, b.y);
      x.stroke();
    }
    cleared() {
      try {
        const d = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
        let clear = 0; let total = 0;
        for (let i = 3; i < d.length; i += 64) { total += 1; if (d[i] < 40) clear += 1; }
        return total ? clear / total : 1;
      } catch (e) { return 1; }
    }
    autoScratch(fromKeyboard) {
      if (this.revealed || this.animating) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.ctx || !this.w || reduce) { this.reveal({ focus: fromKeyboard }); return; }
      this.animating = true;
      const w = this.w; const h = this.h;
      const pts = [];
      const passes = 8;
      for (let i = 0; i <= passes; i += 1) pts.push({ x: -6 + (i / passes) * (w + 12), y: i % 2 ? h * 0.9 : h * 0.1 });
      const seg = pts.length - 1;
      const duration = 600;
      const t0 = performance.now();
      let idx = 0;
      let last = pts[0];
      const lw = h * 0.8;
      const step = (now) => {
        if (this.revealed) return;
        const k = Math.min(1, (now - t0) / duration);
        const pos = k * seg;
        const i = Math.min(seg - 1, Math.floor(pos));
        while (idx < i) { this.stroke(last, pts[idx + 1], lw); last = pts[idx + 1]; idx += 1; }
        const f = pos - i;
        const cur = { x: pts[i].x + (pts[i + 1].x - pts[i].x) * f, y: pts[i].y + (pts[i + 1].y - pts[i].y) * f };
        this.stroke(last, cur, lw);
        last = cur;
        if (k < 1) requestAnimationFrame(step);
        else this.reveal({ focus: fromKeyboard });
      };
      requestAnimationFrame(step);
      this.fallback = setTimeout(() => this.reveal({ focus: fromKeyboard }), duration + 500);
    }
    reveal({ silent = false, focus = false } = {}) {
      if (this.revealed) return;
      this.revealed = true;
      this.animating = false;
      clearTimeout(this.fallback);
      this.classList.add('is-revealed');
      const front = $('[data-scratch-trigger]', this);
      const back = $('.coupon__back', this);
      const code = $('.coupon__code', this);
      if (back) back.hidden = false;
      if (front) front.hidden = true;
      if (code) {
        code.removeAttribute('tabindex');
        code.removeAttribute('aria-hidden');
        if (focus) code.focus();
      }
      if (silent) return;
      try { if (this.dataset.remember === 'true') localStorage.setItem(this.key, '1'); } catch (e) { /* ignore */ }
      if (this.dataset.autoApply === 'true') {
        const root = (theme.routes.root || '/').replace(/\/?$/, '/');
        fetch(`${root}discount/${encodeURIComponent(this.dataset.code)}`, { credentials: 'same-origin' }).catch(() => {});
      }
    }
  }
  customElements.define('coupon-scratch', CouponScratch);

  /* ---------------------------------------------------------------- Partage */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    e.preventDefault();
    const data = { title: document.title, url: btn.dataset.share || window.location.href };
    if (navigator.share) { try { await navigator.share(data); } catch (err) { /* annulé */ } }
    else { try { await navigator.clipboard.writeText(data.url); toast(theme.strings.copied); } catch (err) { /* ignore */ } }
  });

  /* ---------------------------------------------------------------- Carrousel */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-slider]', this);
      if (!this.track) return;
      this.prev = $('[data-slider-prev]', this);
      this.next = $('[data-slider-next]', this);
      this.progress = $('[data-slider-progress]', this);
      this.prev?.addEventListener('click', () => this.move(-1));
      this.next?.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      window.addEventListener('resize', debounce(() => this.update(), 150));
      this.update();
    }
    move(dir) {
      const item = this.track.firstElementChild;
      const w = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth;
      const perView = Math.max(1, Math.round(this.track.clientWidth / w));
      this.track.scrollBy({ left: dir * w * perView, behavior: 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 4;
      if (this.next) this.next.disabled = x >= max - 4;
      this.classList.toggle('is-static', max <= 4);
      if (this.progress) {
        const ratio = this.track.clientWidth / this.track.scrollWidth;
        const pct = Math.min(100, ratio * 100);
        const span = this.progress.firstElementChild;
        span.style.setProperty('--progress', `${pct}%`);
        span.style.setProperty('--offset', `${max > 0 ? (x / max) * ((100 - pct) / pct) * 100 : 0}%`);
      }
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------------------------------------------------------------- Diaporama hero */
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-slide]', this);
      this.dots = $$('[data-dot]', this);
      this.index = 0;
      this.delay = parseInt(this.dataset.speed, 10) * 1000 || 6000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      $('[data-hero-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
      $('[data-hero-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i, true)));
      this.pauseBtn = $('[data-hero-pause]', this);
      this.pauseBtn?.addEventListener('click', () => this.togglePause());
      this.addEventListener('focusin', () => this.stop());
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.autoplay = false;
      this.go(0);
      if (this.autoplay) this.play();
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.stop(); this.go(i); }
      });
    }
    go(i, user) {
      if (!this.slides.length) return;
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => {
        const on = n === this.index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        $$('a, button', s).forEach((el) => { el.tabIndex = on ? 0 : -1; });
        const video = $('video', s);
        if (video) { if (on) video.play().catch(() => {}); else video.pause(); }
      });
      this.dots.forEach((d, n) => d.setAttribute('aria-current', n === this.index ? 'true' : 'false'));
      this.style.setProperty('--hero-duration', `${this.delay}ms`);
      if (user && this.autoplay && !this.paused) this.play();
    }
    play() { this.stop(); this.classList.add('is-playing'); this.timer = setInterval(() => this.go(this.index + 1), this.delay); }
    stop() { clearInterval(this.timer); this.classList.remove('is-playing'); }
    togglePause() {
      this.paused = !this.paused;
      this.pauseBtn.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      if (this.paused) this.stop(); else this.play();
    }
  }
  customElements.define('hero-slideshow', HeroSlideshow);

  /* ---------------------------------------------------------------- Onglets */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.dropdown = $('[data-tabs-select]', this);
      if (this.dropdown) {
        this.dropdown.addEventListener('change', () => this.select(parseInt(this.dropdown.value, 10) || 0));
        const current = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
        this.dropdown.value = String(current);
        this.syncDropdownIcon(current);
      }
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); this.select((i + 1) % this.tabs.length, true); }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); this.select((i - 1 + this.tabs.length) % this.tabs.length, true); }
        });
      });
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.tabs.findIndex((t) => t.dataset.blockId === e.detail.blockId);
        if (i > -1) this.select(i);
      });
    }
    select(i, focus) {
      this.tabs.forEach((tab, n) => {
        const on = n === i;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) { panel.hidden = !on; if (on) { initReveal(panel); $$('slider-component', panel).forEach((s) => s.update && s.update()); } }
      });
      if (focus) this.tabs[i].focus();
      if (this.dropdown) { this.dropdown.value = String(i); this.syncDropdownIcon(i); }
      this.dispatchEvent(new CustomEvent('tabs:change', { detail: { index: i } }));
    }
    syncDropdownIcon(i) {
      // Reprend l'icône de l'onglet choisi à gauche du menu déroulant.
      const holder = $('[data-tabs-select-icon]', this);
      if (!holder) return;
      const src = this.tabs[i] && this.tabs[i].querySelector('svg, img');
      holder.replaceChildren(...(src ? [src.cloneNode(true)] : []));
      holder.closest('.tabs-select').classList.toggle('tabs-select--no-icon', !src);
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ---------------------------------------------------------------- Bandeau d'annonces */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-announcement]', this);
      if (this.items.length < 2) return;
      this.i = 0;
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      $('[data-ann-prev]', this)?.addEventListener('click', () => { this.show(this.i - 1); this.restart(); });
      $('[data-ann-next]', this)?.addEventListener('click', () => { this.show(this.i + 1); this.restart(); });
      this.addEventListener('mouseenter', () => clearInterval(this.t));
      this.addEventListener('mouseleave', () => this.restart());
      this.restart();
    }
    show(i) {
      this.i = (i + this.items.length) % this.items.length;
      this.items.forEach((el, n) => { el.classList.toggle('is-active', n === this.i); el.setAttribute('aria-hidden', n === this.i ? 'false' : 'true'); });
    }
    restart() { clearInterval(this.t); if (this.dataset.autoplay === 'true') this.t = setInterval(() => this.show(this.i + 1), this.speed); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------------------------------------------------------------- En-tête */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.shopify-section') || this;
      this.mode = this.dataset.sticky;
      this.last = 0;
      if (this.mode !== 'none') {
        this.wrapper.classList.add('header-sticky');
        window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      }
      // Méga-menus : ouverture au survol sur ordinateur
      // Le panneau est un descendant du <li> : entrer dans le panneau annule la fermeture différée.
      const canHover = () => window.matchMedia('(hover: hover)').matches;
      $$('details[data-mega]', this).forEach((d) => {
        const li = d.parentElement;
        const cancelClose = () => clearTimeout(d._closeTimer);
        li.addEventListener('mouseenter', () => { if (!canHover()) return; cancelClose(); this.openMenu(d); });
        li.addEventListener('mouseleave', () => {
          if (!canHover()) return;
          cancelClose();
          d._closeTimer = setTimeout(() => d.removeAttribute('open'), 280);
        });
        d.addEventListener('toggle', () => { if (d.open) this.closeOthers(d); });
        $('summary', d).addEventListener('click', (e) => { if (canHover() && d.open) e.preventDefault(); });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) $$('details[data-mega][open]', this).forEach((d) => d.removeAttribute('open')); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') $$('details[data-mega][open]', this).forEach((d) => { d.removeAttribute('open'); $('summary', d).focus(); }); });
    }
    openMenu(d) { this.closeOthers(d); d.setAttribute('open', ''); }
    closeOthers(d) { $$('details[data-mega][open]', this).forEach((o) => { if (o !== d) o.removeAttribute('open'); }); }
    onScroll() {
      const y = window.scrollY;
      this.wrapper.classList.toggle('is-scrolled', y > 10);
      if (this.mode === 'on-scroll-up') {
        const hide = y > this.last && y > 200 && !$('details[open]', this);
        this.wrapper.classList.toggle('is-hidden', hide);
      }
      this.last = y;
    }
  }
  customElements.define('site-header', SiteHeader);

  /* ---------------------------------------------------------------- Recherche prédictive */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      this.defaults = $('[data-predictive-defaults]', this);
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      $$('[data-suggestion]', this).forEach((b) => b.addEventListener('click', () => { this.input.value = b.dataset.suggestion; this.search(); this.input.focus(); }));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; if (this.defaults) this.defaults.hidden = false; return; }
      this.abort?.abort();
      this.abort = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,query,article&resources[limit]=6&resources[options][unavailable_products]=last`;
        const html = await (await fetch(url, { signal: this.abort.signal })).text();
        const inner = $('[data-predictive-inner]', parseHTML(html));
        this.results.innerHTML = inner ? inner.innerHTML : '';
        if (this.defaults) this.defaults.hidden = true;
      } catch (e) { /* annulée */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------------------------------------------------------------- Ajout rapide */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (variantId) {
      btn.classList.add('is-loading');
      const fd = new FormData(); fd.append('id', variantId); fd.append('quantity', '1');
      try { await Cart.add(fd); btn.classList.add('is-added'); setTimeout(() => btn.classList.remove('is-added'), 1500); Cart.afterAdd(btn); }
      catch (err) { toast(err.message); }
      finally { btn.classList.remove('is-loading'); }
      return;
    }
    const drawer = document.getElementById('QuickAdd');
    if (!drawer) { window.location.href = btn.dataset.productUrl; return; }
    const body = $('[data-quick-add-body]', drawer);
    body.innerHTML = '<div class="quick-add__loading" aria-busy="true"></div>';
    drawer.open(btn);
    try {
      const html = await (await fetch(`${btn.dataset.productUrl}${btn.dataset.productUrl.includes('?') ? '&' : '?'}section_id=quick-add`)).text();
      const inner = $('[data-quick-add-inner]', parseHTML(html));
      body.innerHTML = inner ? inner.innerHTML : '';
    } catch (err) { window.location.href = btn.dataset.productUrl; }
  });

  /* ---------------------------------------------------------------- Recommandations produit */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { io.disconnect(); this.load(); } }, { rootMargin: '400px 0px' });
      io.observe(this);
    }
    async load() {
      try {
        const html = await (await fetch(this.dataset.url)).text();
        const inner = $('product-recommendations', parseHTML(html));
        if (inner && inner.innerHTML.trim().length && $('.product-card, .upsell-item', inner)) {
          this.innerHTML = inner.innerHTML;
          this.closest('[data-reco-wrapper]')?.removeAttribute('hidden');
          initReveal(this);
          $$('slider-component', this).forEach((s) => s.update && s.update());
        } else if (this.dataset.hideEmpty === 'true') {
          (this.closest('[data-reco-wrapper]') || this.closest('.shopify-section'))?.setAttribute('hidden', '');
        }
      } catch (e) { /* ignore */ }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------------------------------------------------------------- Récemment consultés */
  const RV_KEY = 'yoka:recently-viewed';
  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const current = this.dataset.current;
      let list = [];
      try { list = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (e) { list = []; }
      const toShow = list.filter((h) => h !== current).slice(0, parseInt(this.dataset.limit, 10) || 6);
      if (current) { try { localStorage.setItem(RV_KEY, JSON.stringify([current, ...list.filter((h) => h !== current)].slice(0, 16))); } catch (e) { /* ignore */ } }
      if (!toShow.length) return;
      this.render(toShow);
    }
    async render(handles) {
      const track = $('[data-slider]', this);
      const cards = await Promise.all(handles.map(async (h) => {
        try {
          const res = await fetch(`${theme.routes.root.replace(/\/$/, '')}/products/${h}?section_id=product-card-render`);
          if (!res.ok) return '';
          const inner = $('[data-card-inner]', parseHTML(await res.text()));
          return inner ? `<div class="recently-viewed__item">${inner.innerHTML}</div>` : '';
        } catch (e) { return ''; }
      }));
      const html = cards.filter(Boolean).join('');
      if (!html) return;
      track.innerHTML = html;
      this.hidden = false;
      $('slider-component', this)?.update?.();
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ---------------------------------------------------------------- Filtres de collection */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.onInput = debounce(() => this.submit(), 450);
      this.addEventListener('change', (e) => { if (e.target.closest('form[data-facets-form]')) this.onInput(); });
      this.addEventListener('submit', (e) => { if (e.target.matches('form[data-facets-form]')) { e.preventDefault(); this.submit(); } });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.load(new URL(link.href, window.location.origin).searchParams.toString(), true, !!link.closest('.pagination')); }
      });
      window.addEventListener('popstate', () => this.load(window.location.search.slice(1), false));
    }
    params() {
      const forms = $$('form[data-facets-form]', this);
      const merged = new URLSearchParams();
      const seen = new Set();
      forms.forEach((f) => {
        new FormData(f).forEach((v, k) => {
          const pair = `${k}=${v}`;
          if (v === '' || seen.has(pair)) return;
          seen.add(pair);
          merged.append(k, v);
        });
      });
      return merged.toString();
    }
    submit() { this.load(this.params()); }
    async load(query, push = true, toTop = false) {
      const grid = $('[data-facets-results]', this);
      const activeId = document.activeElement && document.activeElement.id;
      grid?.classList.add('is-loading');
      try {
        const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
        const doc = parseHTML(await (await fetch(url)).text());
        ['[data-facets-results]', '[data-facets-active]', '[data-facets-count]', '[data-facets-sidebar]', '[data-facets-drawer-body]'].forEach((sel) => {
          const fresh = $(sel, doc); const cur = $(sel, this);
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
        });
        if (push) window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
        // Changement de page : on remonte en haut de la liste, comme lors d'un vrai chargement.
        if (toTop) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: Math.max(0, this.getBoundingClientRect().top + window.scrollY - 96), behavior: reduce ? 'auto' : 'smooth' });
          const grid = $('[data-facets-results]', this);
          if (grid) { grid.setAttribute('tabindex', '-1'); grid.focus({ preventScroll: true }); }
        }
        initReveal(this);
      } catch (e) { window.location.search = query; }
      finally { $('[data-facets-results]', this)?.classList.remove('is-loading'); }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------------------------------------------------------------- Points d'intérêt (anatomie produit) */
  class HotspotImage extends HTMLElement {
    connectedCallback() {
      this.pins = $$('[data-hotspot]', this);
      this.pins.forEach((pin) => pin.addEventListener('click', () => this.toggle(pin)));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeAll(); });
      $$('[data-hotspot-list] [data-target]', this.closest('.shopify-section') || this).forEach((item) => {
        item.addEventListener('mouseenter', () => { const pin = document.getElementById(item.dataset.target); if (pin) this.toggle(pin, true); });
      });
    }
    toggle(pin, forceOpen) {
      const open = forceOpen || pin.getAttribute('aria-expanded') !== 'true';
      this.closeAll();
      pin.setAttribute('aria-expanded', open ? 'true' : 'false');
      const panel = document.getElementById(pin.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
    }
    closeAll() {
      this.pins.forEach((p) => {
        p.setAttribute('aria-expanded', 'false');
        const panel = document.getElementById(p.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
    }
  }
  customElements.define('hotspot-image', HotspotImage);

  /* ---------------------------------------------------------------- Vidéo différée (clic pour lancer) */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const btn = $('[data-deferred-play]', this);
      btn?.addEventListener('click', () => this.load());
    }
    load() {
      const tpl = $('template', this);
      if (!tpl || this.loaded) return;
      this.loaded = true;
      const content = tpl.content.firstElementChild.cloneNode(true);
      this.innerHTML = '';
      this.appendChild(content);
      if (content.tagName === 'VIDEO') content.play().catch(() => {});
    }
  }
  customElements.define('deferred-media', DeferredMedia);

  /* ------------------------------ « Tout ce qu'il vous faut » : la catégorie touchée reste en place, puis se montre en entier
     1. Pendant que la catégorie déjà ouverte se replie (au-dessus), on compense le défilement image par image :
        la ligne qu'on vient de toucher ne bouge pas sous le doigt ou la souris.
     2. Une fois l'ouverture terminée, et seulement si la catégorie dépasse de l'écran, un seul mouvement doux la centre
        (ou l'aligne sous l'en-tête si elle est plus haute que l'écran). Déjà entièrement visible : rien ne bouge.
     Si l'utilisateur fait défiler pendant ce temps, on lui rend la main aussitôt. */
  document.addEventListener('click', (e) => {
    const summary = e.target.closest && e.target.closest('.checklist__summary');
    if (!summary) return;
    const item = summary.parentElement;
    if (!item || item.open) return; // seulement à l'ouverture
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const root = document.documentElement;
    const startTop = summary.getBoundingClientRect().top;
    const prevAnchor = root.style.overflowAnchor;
    root.style.overflowAnchor = 'none'; // la compensation est faite ici, pas par le navigateur
    let aborted = false;
    const abort = () => { aborted = true; };
    window.addEventListener('wheel', abort, { passive: true, once: true });
    window.addEventListener('touchmove', abort, { passive: true, once: true });
    const done = () => {
      root.style.overflowAnchor = prevAnchor;
      window.removeEventListener('wheel', abort);
      window.removeEventListener('touchmove', abort);
    };
    const reveal = () => {
      if (aborted || !item.open) return;
      const r = item.getBoundingClientRect();
      const header = $('.header-sticky');
      const headerH = header && !header.classList.contains('is-hidden') ? header.offsetHeight : 0;
      const place = (offset) => {
        const top = offset + 12;
        const room = window.innerHeight - 12 - top;
        if (r.top >= top && r.bottom <= window.innerHeight - 12) return 0;
        return r.height <= room ? (r.top + r.height / 2) - (top + room / 2) : r.top - top;
      };
      // En descendant, l'en-tête se masque : il ne compte pas. En remontant, il réapparaît : on lui laisse sa place.
      let delta = place(0);
      if (delta < 0) delta = place(headerH);
      if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: reduce ? 'instant' : 'smooth' });
    };
    const t0 = performance.now();
    const duration = reduce ? 0 : 520; // repli et dépli de 0,45 s, plus une marge
    const pin = (now) => {
      if (aborted) return done();
      const dy = summary.getBoundingClientRect().top - startTop;
      // « instant » : le site défile en douceur par défaut (scroll-behavior: smooth), ce qui ferait traîner la compensation.
      if (Math.abs(dy) > 0.5) window.scrollBy({ top: dy, behavior: 'instant' });
      if (now - t0 < duration) return requestAnimationFrame(pin);
      done();
      reveal();
    };
    requestAnimationFrame(pin);
  });
})();
